
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat, useCompletion, type UIMessage as AiMessage } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { RefreshCw, Sparkles, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { ChatHeader } from "./ChatHeader";
import { MessageComposer } from "./MessageComposer";
import { MessageList } from "./MessageList";
import { useMcpContext } from "@/hooks/useMcpContext";
import type { ChatMessage, LLMSettings, MCPServer, MCPTool, MCPConfig, MCPContextSnapshot } from "@/types";
import { loadTools, saveTools } from "@/services/db";
import { Button } from "@/components/ui/button";
import { MCPClient } from "@/services/mcpClient";
import { loadMCPConfig } from "@/services/settings";
import { log } from "@/lib/utils";

interface ChatWindowProps {
  contact?: MCPServer;
  threadId?: string;
  initialMessages: ChatMessage[];
  onMessagesChange: (messages: ChatMessage[]) => void;
  llmSettings: LLMSettings;
  onOpenSettings?: (contact: MCPServer) => void;
  onOpenAccount?: () => void;
  onServerStatusChange?: (id: string, status: MCPServer["status"]) => void;
  onLlmSettingsChange?: (settings: LLMSettings) => void;
}

const normalizeContent = (message: AiMessage) => {
  const normalized = (message.parts ?? [])
    .map(part => {
      if (part.type === "text") return part.text;
      if (part.type === "reasoning") return part.text ?? "";
      
      // tool-input-available or dynamic-tool with input-available state
      if (part.type === "dynamic-tool" && (part as any).state === "input-available") {
        const toolName = part.title ?? part.toolName ?? "tool";
        const input = part.input ?? (part as any).input;
        const inputPreview = input ? JSON.stringify(input) : "";
        return `Calling ${toolName}${inputPreview ? ` with ${inputPreview}` : ""}…`;
      }
      
      // tool-output variants (provider dependent)
      if (part.type === "dynamic-tool" && ((part as any).state === "output-available" || (part as any).state === "output-error")) {
        const toolName = part.title ?? part.toolName ?? (part as any).toolName ?? "tool";
        
        // Check for error state first
        const errorText = (part as any).errorText;
        if (errorText) {
          return `⚠️ Error from **${toolName}**: ${errorText}`;
        }
        
        // First check if output is directly on the part (common case)
        const directOutput = (part as any).output;
        // Then check state object (fallback for other formats)
        const state = (part as any).state ?? {};
        const output = directOutput ?? state.output ?? state.result ?? state.text ?? state.data ?? state;
        const text =
          typeof output === "string"
            ? output
            : output && typeof output === "object"
              ? JSON.stringify(output)
              : String(output ?? "");
        return text ? `Result from **${toolName}**: ${text}` : "";
      }
      return "";
    })
    .filter(Boolean)
    .join(" ");

  // Fallback to message.content when parts are empty/missing
  if (!normalized && typeof (message as any).content === "string") {
    return (message as any).content as string;
  }

  return normalized;
};

const toAiMessages = (messages: ChatMessage[]): AiMessage[] =>
  messages.map(msg => ({
    id: msg.id,
    role: msg.author === "agent" ? "assistant" : msg.author,
    parts: [{ type: "text", text: msg.content }],
    metadata: { tags: msg.tags, status: msg.status, contactId: msg.contactId },
  }));

export const ChatWindow = ({
  contact,
  threadId,
  initialMessages,
  onMessagesChange,
  llmSettings,
  onOpenSettings,
  onServerStatusChange,
  onLlmSettingsChange,
  onOpenAccount,
}: ChatWindowProps) => {
  const { snapshot, loading: contextLoading, refreshing: contextRefreshing, error: contextError, refresh, loadFromCache } = useMcpContext(contact);
  const [tools, setTools] = useState<MCPTool[]>([]);
  const [toolsLoaded, setToolsLoaded] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isContextOpen, setIsContextOpen] = useState(false);
  const [connectionIssue, setConnectionIssue] = useState<string | null>(null);
  const [systemMessages, setSystemMessages] = useState<ChatMessage[]>([]);
  const [persistentCompletion, setPersistentCompletion] = useState<string | null>(null);
  const createdAtRef = useRef<Record<string, string>>({});
  const retryAttemptRef = useRef(0);
  const messageTagsRef = useRef<Record<string, string[]>>({});
  const lastUsedTagsRef = useRef<string[]>([]);
  const prevMessagesRef = useRef<ChatMessage[]>([]);
  const lastToastRef = useRef<string | null>(null);
  const keepAliveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mcpClientRef = useRef<MCPClient>(new MCPClient());
  const serverStatusCbRef = useRef<typeof onServerStatusChange>(onServerStatusChange);
  const [mcpConfig, setMcpConfig] = useState<MCPConfig>(loadMCPConfig());

  useEffect(() => {
    serverStatusCbRef.current = onServerStatusChange;
  }, [onServerStatusChange]);

  // Listen for MCP config updates from settings panel
  useEffect(() => {
    const handleConfigUpdate = (event: CustomEvent<MCPConfig>) => {
      setMcpConfig(event.detail);
      log.info("[ChatWindow] MCP config updated", event.detail);
    };
    window.addEventListener("mcp-config-updated" as any, handleConfigUpdate);
    return () => window.removeEventListener("mcp-config-updated" as any, handleConfigUpdate);
  }, []);

  // Reset state when contact changes
  useEffect(() => {
    setTools([]);
    setToolsLoaded(false);
    setSelectedTags([]);
    setConnectionIssue(null);
    setIsContextOpen(false);
    setSystemMessages([]);
    setPersistentCompletion(null);
    retryAttemptRef.current = 0;
    createdAtRef.current = {};
    messageTagsRef.current = {};
    prevMessagesRef.current = [];
    if (initialMessages.length) {
      initialMessages.forEach(msg => {
        createdAtRef.current[msg.id] = msg.createdAt;
        if (msg.tags?.length) {
          messageTagsRef.current[msg.id] = msg.tags;
        }
      });
    }
  }, [contact?.id]); // reset only when switching contacts to avoid update loops while streaming

  /**
   * Load tools from cache when context is ready
   */
  useEffect(() => {
    if (snapshot?.tools?.length) {
      setTools(snapshot.tools);
      setToolsLoaded(true);
      log.debug(`[ChatWindow] Loaded tools from context for ${contact!.name} (${contact!.id})`);
    }
  }, [snapshot?.tools]);

  const handleToolsOpen = useCallback(async () => {
    // Skip if tools are already loaded
    if (!contact || toolsLoaded) return;
    const loaded = await loadTools(contact.id);
    setTools(loaded);
    setToolsLoaded(true);
  }, [contact, toolsLoaded]);

  const handleToolsChange = useCallback(
    async (next: MCPTool[]) => {
      setTools(next);
      if (contact) await saveTools(contact.id, next);
    },
    [contact],
  );

  useEffect(() => {
    if (!contact) return;
    
    // Skip ping cycle if keep-alive is disabled
    if (!mcpConfig.keepAlive) {
      log.info(`[ChatWindow] Keep-alive disabled for ${contact.name}, skipping ping cycle`);
      return;
    }

    const MAX_ATTEMPTS = mcpConfig.maxAttempts;
    const BASE_DELAY = mcpConfig.baseDelay * 1000; // Convert to ms
    const MAX_DELAY = mcpConfig.maxDelay * 1000;  // Convert to ms
    // Store contact in a ref to avoid stale closures
    const contactRef = { current: contact };

    const clearTimer = () => {
      if (keepAliveRef.current) {
        clearTimeout(keepAliveRef.current);
        keepAliveRef.current = null;
      }
    };

    const schedule = (delay: number) => {
      clearTimer();
      keepAliveRef.current = setTimeout(runPing, delay);
    };

    const runPing = async () => {
      const currentContact = contactRef.current;
      if (!currentContact) return;

      const { success, message } = await mcpClientRef.current.ping(currentContact);

      if (success) {
        retryAttemptRef.current = 0;
        setConnectionIssue(null);
        serverStatusCbRef.current?.(currentContact.id, "online");
        schedule(BASE_DELAY);
        return;
      }

      log.warn(`[ChatWindow] Ping failed for ${currentContact.name}`);
      serverStatusCbRef.current?.(currentContact.id, "offline");
      retryAttemptRef.current += 1;
      const attempt = retryAttemptRef.current;
      const msg =
        attempt >= MAX_ATTEMPTS
          ? `Unable to reach ${currentContact.name} after ${MAX_ATTEMPTS} attempts. Check network/VPN or MCP URL, then refresh.`
          : `Cannot reach ${currentContact.name}. Retrying (${attempt}/${MAX_ATTEMPTS})…`;
      const withReason = message ? `${msg} (${message})` : msg;
      setConnectionIssue(withReason);
      if (lastToastRef.current !== withReason) {
        toast.error(withReason);
        lastToastRef.current = withReason;
      }

      if (attempt < MAX_ATTEMPTS) {
        const nextDelay = Math.min(BASE_DELAY * Math.pow(2, attempt - 1), MAX_DELAY);
        schedule(nextDelay);
      } else {
        log.warn(`[ChatWindow] Max attempts reached for ${currentContact.name}, stopping ping cycle`);
      }
    };

    runPing();

    return () => {
      clearTimer();
    };
  }, [contact?.id, mcpConfig.keepAlive, mcpConfig.baseDelay, mcpConfig.maxDelay, mcpConfig.maxAttempts]); // Re-run when contact or config changes

  const initialAiMessages = useMemo(() => toAiMessages(initialMessages), [initialMessages]);
  const [input, setInput] = useState("");

  const contextForRequest = useMemo((): MCPContextSnapshot | undefined => {
    if (!contact) return undefined;
    const base =
      snapshot ??
      ({
        serverId: contact.id,
        serverName: contact.name,
        role: contact.role,
        tools: [],
        resources: [],
        fetchedAt: new Date().toISOString(),
      } as MCPContextSnapshot);

    return {
      ...base,
      serverId: contact.id,
      serverName: base.serverName ?? contact.name,
      role: base.role ?? contact.role,
      tools: tools.length ? tools : base.tools ?? [],
      resources: base.resources ?? [],
    };
  }, [contact, snapshot, tools]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport<AiMessage>({
        api: "/api/ai/chat",
        body: {
          server: contact,
          tags: selectedTags,
          contextSnapshot: contextForRequest,
          llm: llmSettings,
        },
      }),
    [contact, llmSettings, selectedTags, contextForRequest],
  );

  const { messages: aiMessages, sendMessage, stop, status, error: chatError } = useChat({
    // Scope conversation to the specific thread when available
    id: threadId ?? contact?.id,
    messages: initialAiMessages,
    transport,
  });

  const chatMessages: ChatMessage[] = useMemo(() => {
    return aiMessages.map((msg, index) => {
      const id = msg.id ?? `${contact?.id ?? "msg"}-${index}`;
      if (!createdAtRef.current[id]) {
        createdAtRef.current[id] = new Date().toISOString();
      }
            
      const tagsFromMessage = Array.isArray((msg.metadata as any)?.tags)
        ? ((msg.metadata as any).tags as string[])
        : undefined;
      const tags =
        messageTagsRef.current[id] ??
        tagsFromMessage ??
        (msg.role === "assistant" && lastUsedTagsRef.current.length ? lastUsedTagsRef.current : undefined);
      if (tags && !messageTagsRef.current[id]) {
        messageTagsRef.current[id] = tags;
      }

      const mappedAuthor: ChatMessage["author"] =
        msg.role === "assistant" ? "agent" : msg.role === "user" ? "user" : "system";

      const content = normalizeContent(msg);

      return {
        id,
        contactId: contact?.id ?? "unknown",
        threadId: threadId ?? undefined,
        author: mappedAuthor,
        content,
        createdAt: createdAtRef.current[id],
        status:
          msg.role === "assistant" && (status === "streaming" || status === "submitted") && index === aiMessages.length - 1
            ? "streaming"
            : "sent",
        tags,
        meta: (msg.metadata as Record<string, unknown>) ?? {},
      };
    });
  }, [aiMessages, contact?.id, status]);

  const combinedMessages = useMemo(() => {
    const merged = [...systemMessages, ...chatMessages];
    return merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [chatMessages, systemMessages]);

  useEffect(() => {
    if (!contact) return;
    
    // Only update if messages actually changed (prevent infinite loop)
    const messagesChanged = 
      combinedMessages.length !== prevMessagesRef.current.length ||
      combinedMessages.some((msg, idx) => {
        const prev = prevMessagesRef.current[idx];
        return !prev || 
          msg.id !== prev.id || 
          msg.content !== prev.content || 
          msg.status !== prev.status;
      });
    
    if (messagesChanged) {
      prevMessagesRef.current = combinedMessages;
      onMessagesChange(combinedMessages);
    }
  }, [combinedMessages, contact, onMessagesChange]);

  // Dev logging to observe what reaches the UI layer
  // useEffect(() => {
  //   if (!aiMessages.length) return;
  //   const last = aiMessages[aiMessages.length - 1];
    // log.debug("[chat-ui] message update", {
    //   status,
    //   total: aiMessages.length,
    //   role: last?.role,
    //   parts: last?.parts?.map(part => part.type),
    // });
  // }, [aiMessages, status]);

  useEffect(() => {
    if (contextError && lastToastRef.current !== contextError) {
      toast.error("MCP context unavailable", { description: contextError });
      lastToastRef.current = contextError;
    } else if (snapshot?.error && lastToastRef.current !== snapshot.error) {
      toast.error("MCP sync issue", { description: snapshot.error });
      lastToastRef.current = snapshot.error;
    } else if (chatError && lastToastRef.current !== chatError.message) {
      toast.error("Chat failed", { description: chatError.message });
      lastToastRef.current = chatError.message;
    }
  }, [contextError, snapshot?.error, chatError]);

  const pushSystemNotice = (content: string, meta: Record<string, unknown> = {}) => {
    if (!contact) return;
    const id = `system-${Date.now()}`;
    const createdAt = new Date().toISOString();
    createdAtRef.current[id] = createdAt;
    if (selectedTags.length) {
      messageTagsRef.current[id] = [...selectedTags];
    }
    setSystemMessages(prev => [
      ...prev,
      {
        id,
        contactId: contact.id,
        author: "system",
        content,
        createdAt,
        status: "error",
        tags: selectedTags.length ? [...selectedTags] : undefined,
        meta,
      },
    ]);
  };

  const handleSendMessage = async () => {
    if (!contact) return;
    const trimmed = input.trim();
    if (!trimmed) return;
    const guardReason =
      connectionIssue ??
      snapshot?.error ??
      contextError ??
      null;
    if (guardReason) {
      const msg = `Cannot send message: ${guardReason}`;
      if (lastToastRef.current !== msg) {
        toast.error("MCP unavailable", { description: guardReason });
        lastToastRef.current = msg;
      }
      pushSystemNotice(msg, { connection: false });
      setInput("");
      return;
    }

    const { success: reachable, message: pingMessage } = await mcpClientRef.current.ping(contact);
    if (!reachable) {
      const reason = pingMessage ?? `Cannot reach ${contact.name}. Check internet/VPN or MCP URL, then retry.`;
      setConnectionIssue(reason);
      retryAttemptRef.current += 1;
      onServerStatusChange?.(contact.id, "offline");
      if (lastToastRef.current !== reason) {
        toast.error("MCP offline", { description: reason });
        lastToastRef.current = reason;
      }
      pushSystemNotice(reason, { connection: false, attempt: retryAttemptRef.current });
      setInput("");
      return;
    }

    const tagsSnapshot = [...selectedTags];
    lastUsedTagsRef.current = tagsSnapshot;
    setConnectionIssue(null);
    onServerStatusChange?.(contact.id, "online");
    const userId = `user-${Date.now()}`;
    createdAtRef.current[userId] = new Date().toISOString();
    if (tagsSnapshot.length) {
      messageTagsRef.current[userId] = tagsSnapshot;
    }
    try {
      await sendMessage({
        id: userId,
        role: "user",
        parts: [{ type: "text", text: trimmed }],
        metadata: { tags: tagsSnapshot },
      });
    } catch (error: any) {
      const errorId = `error-${Date.now()}`;
      const createdAt = new Date().toISOString();
      createdAtRef.current[errorId] = createdAt;
      messageTagsRef.current[errorId] = tagsSnapshot;
      onMessagesChange([
        ...chatMessages,
        {
          id: errorId,
          contactId: contact.id,
          author: "system",
          content: error?.message ?? "Chat request failed",
          createdAt,
          status: "error",
          tags: tagsSnapshot,
          meta: { error: true },
        },
      ]);
    }
    setInput("");
  };

  const isStreaming = status === "streaming" || status === "submitted";

  const { completion, complete, isLoading: isSummarizing } = useCompletion({
    api: "/api/ai/context",
    body: {
      server: contact,
      tags: selectedTags,
      contextSnapshot: snapshot,
      llm: llmSettings,
    },
  });

  const summarizeContext = () => {
    if (!contact) return;
    complete("Summarize MCP context for quick reading.");
  };

  const displayMessages = useMemo(() => {
    const base = [...combinedMessages];
    if (isStreaming && (base.length === 0 || base[base.length - 1]?.author !== "agent")) {
      base.push({
        id: `placeholder-${contact?.id ?? "unknown"}`,
        contactId: contact?.id ?? "unknown",
        author: "agent",
        content: "…",
        createdAt: new Date().toISOString(),
        status: "streaming",
        tags: lastUsedTagsRef.current.length ? [...lastUsedTagsRef.current] : undefined,
        meta: { placeholder: true },
      });
    }
    return base;
  }, [combinedMessages, contact?.id, isStreaming]);

  const handleModelChange = (model: string, provider: LLMSettings["provider"]) => {
    if (!onLlmSettingsChange) return;
    
    const updatedSettings: LLMSettings = {
      ...llmSettings,
      model,
      provider,
    };
    
    onLlmSettingsChange(updatedSettings);
    log.info(`[ChatWindow] Model changed to ${model} (${provider})`);
  };

  if (!contact) {
    return (
      <section className="glass-card flex min-h-[70vh] flex-col items-center justify-center gap-2 sm:gap-3 border border-white/40 p-4 sm:p-6 text-center shadow-xl dark:border-white/10">
        <p className="text-gradient text-base sm:text-lg font-semibold">Pick an MCP server to start chatting.</p>
        <p className="max-w-md text-xs sm:text-sm text-muted-foreground px-2">
          Each server behaves like an agent with access to different tools and context. Connect to your favorite MCP-compatible
          servers and start a conversation!
        </p>
      </section>
    );
  }

  return (
    <section className="glass-card flex h-[85vh] max-h-[90vh] flex-col gap-2 sm:gap-4 border border-white/40 p-2 sm:p-4 shadow-xl dark:border-white/10">
      {/* Top bar */}
      <>
        <ChatHeader 
          contact={contact} 
          isStreaming={isStreaming} 
          onOpenSettings={onOpenSettings}
          onOpenAccount={onOpenAccount}
          llmSettings={llmSettings}
          onModelChange={handleModelChange}
        />

        {connectionIssue || snapshot?.error || contextError ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs sm:text-sm text-rose-700">
            {connectionIssue ??
              snapshot?.error ??
              contextError}
          </div>
        ) : null}

        {/* MCP Context (collapsible accordion style) */}
        <div className="rounded-xl border border-white/60 bg-white/70 p-2 sm:p-3 shadow-sm dark:border-white/10 dark:bg-white/5">
          <div
            className="grid grid-cols-[auto_1fr_auto] items-center gap-2 cursor-pointer select-none"
            onClick={() => setIsContextOpen(open => !open)}
            aria-expanded={isContextOpen}
            aria-controls="mcp-context-content"
            tabIndex={0}
            role="button"
          >
            {/* Chevron icon */}
            <span className="flex-shrink-0 text-primary">
              {isContextOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </span>
            
            {/* Title */}
            <p className="text-xs sm:text-sm uppercase tracking-wide text-primary font-semibold min-w-0 truncate">
              MCP context
            </p>
            
            {/* Action buttons - icon only on mobile */}
            <div className="flex gap-1.5 sm:gap-2 flex-shrink-0">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={e => { e.stopPropagation(); loadFromCache(); }} 
                disabled={contextLoading}
                className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3"
                title="Reload from cache"
              >
                <RefreshCw className="h-3.5 w-3.5 sm:mr-2" />
                <span className="hidden sm:inline">{contextLoading ? "Loading…" : "Cache"}</span>
              </Button>
              <Button 
                size="sm" 
                variant="secondary" 
                onClick={e => { e.stopPropagation(); refresh(); }} 
                disabled={contextRefreshing}
                className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3"
                title="Refresh from server (updates cache)"
              >
                <RefreshCw className="h-3.5 w-3.5 sm:mr-2" />
                <span className="hidden sm:inline">{contextRefreshing ? "Fetching…" : "Refresh"}</span>
              </Button>
              <Button 
                size="sm" 
                variant="secondary" 
                onClick={e => { e.stopPropagation(); summarizeContext(); }} 
                disabled={isSummarizing}
                className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3"
                title="Summarize context"
              >
                <Sparkles className="h-3.5 w-3.5 sm:mr-2" />
                <span className="hidden sm:inline">{isSummarizing ? "Summarizing…" : "Summarize"}</span>
              </Button>
            </div>
          </div>
          
          {isContextOpen && (
            <div className="mt-3 space-y-2" id="mcp-context-content">
              <p className="text-xs sm:text-sm text-muted-foreground">
                Tools and resources feed the LLM so answers stay grounded.
              </p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {selectedTags.map(tag => (
                  <span key={tag} className="pill bg-primary/10 text-primary text-xs">
                    #{tag}
                  </span>
                ))}
                {!selectedTags.length ? (
                  <span className="text-xs text-muted-foreground">No tags yet. Tag tools or topics for better grounding.</span>
                ) : null}
              </div>
              {contextError ? <p className="text-xs text-rose-600">Context sync failed: {contextError}</p> : null}
              {completion || persistentCompletion ? (
                <p className="text-xs sm:text-sm text-foreground leading-relaxed">{completion || persistentCompletion}</p>
              ) : snapshot ? (
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  Cached {new Date(snapshot.fetchedAt).toLocaleTimeString()} — {snapshot.tools?.length ?? 0} tools,{" "}
                  {snapshot.resources?.length ?? 0} resources.
                  {!snapshot.tools?.length && !snapshot.resources?.length ? " Use 'Refresh' to sync from server." : ""}
                </p>
              ) : null}
            </div>
          )}
        </div>

        {isStreaming ? (
          <div className="flex items-center gap-2 rounded-lg bg-primary/5 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-primary">
            <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" />
            <span className="hidden sm:inline">Processing request…</span>
            <span className="sm:hidden">Processing…</span>
          </div>
        ) : null}
      </>
      <div className="flex-1 flex flex-col min-h-0">
        <MessageList messages={displayMessages} />
        <MessageComposer
          value={input}
          onValueChange={setInput}
          onSend={handleSendMessage}
          disabled={isStreaming}
          tools={tools}
          onToolsChange={handleToolsChange}
          onToolsOpen={handleToolsOpen}
          selectedTags={selectedTags}
          onTagsChange={setSelectedTags}
          onStop={() => stop()}
          isStreaming={isStreaming}
        />
      </div>
    </section>
  );
};
